import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { Product, Category } from "../lib/api";
import ProductCard from "../components/ProductCard";
import HeroCarousel from "../components/HeroCarousel";
import { AiCardPromoBanner } from "../components/AiCardCreator";
import { ArrowRight } from "lucide-react";
import { useTenant } from "../lib/tenant";

export default function HomePage() {
  const { id: tenantId, tTenant } = useTenant();

  const { data: featured = [] } = useQuery<Product[]>({
    queryKey: ["products", { featured: true }],
    queryFn: () => api.get("/products?featured=true").then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: () => api.get("/products/categories").then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const isChlorine = tenantId === "chlorine.art";

  return (
    <main>
      <HeroCarousel />

      {/* Featured products */}
      {featured.length > 0 && (
        <section className={`${isChlorine ? "bg-white" : "bg-gray-50"} py-16`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`flex items-center justify-between mb-8 ${isChlorine ? "flex-col gap-4 text-center" : ""}`}>
              <h2 className="font-display text-2xl font-bold text-gray-900 uppercase tracking-widest">
                {isChlorine ? "Featured Collections" : tTenant("featured.title")}
              </h2>
              <Link to="/shop" className="flex items-center gap-1 text-sm font-medium hover:opacity-70 transition-colors" style={{ color: 'rgb(var(--brand-color))' }}>
                {tTenant("featured.seeAll")} <ArrowRight size={15} />
              </Link>
            </div>
            <div className={`grid gap-8 ${isChlorine ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
              {(isChlorine ? featured.slice(0, 4) : featured.slice(0, 8)).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className={`flex items-center justify-between mb-8 ${isChlorine ? "flex-col gap-4 text-center" : ""}`}>
          <h2 className="font-display text-2xl font-bold text-gray-900 uppercase tracking-widest">
            {isChlorine ? "Shop by Category" : tTenant("categories.title")}
          </h2>
          <Link to="/shop" className="flex items-center gap-1 text-sm font-medium hover:opacity-70 transition-colors" style={{ color: 'rgb(var(--brand-color))' }}>
            {tTenant("categories.viewAll")} <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/shop?category=${cat.slug}`}
              className="flex flex-col items-center gap-2 rounded-xl border border-gray-100 bg-white p-4 text-center shadow-sm hover:border-gray-200 transition-all group"
            >
              <span className="text-3xl">{categoryEmoji(cat.slug)}</span>
              <span className="text-sm font-semibold text-gray-700 group-hover:opacity-70 transition-colors">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Print spotlight */}
      <section className={`relative overflow-hidden py-24 px-4 text-white ${isChlorine ? "bg-black" : "bg-gradient-to-br from-brand-700 via-brand-600 to-pink-600"}`}>
        {!isChlorine && <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent pointer-events-none" />}
        <div className="max-w-5xl mx-auto relative z-10">
          <div className={`grid md:grid-cols-2 gap-16 items-center ${isChlorine ? "text-center md:text-left" : ""}`}>
            <div>
              <span className={`inline-block px-3 py-1 text-[10px] font-bold tracking-[0.3em] uppercase mb-6 border rounded ${isChlorine ? "text-white border-white/30" : "bg-white/20 border-white/40 text-white"}`}>
                {tTenant("print.badge")}
              </span>
              <h2 className="font-display text-3xl sm:text-5xl font-bold mb-6 leading-[1.1] uppercase tracking-tighter">
                {tTenant("print.headline")}
              </h2>
              <p
                className={`leading-relaxed text-lg ${isChlorine ? "text-gray-400" : "text-white/90 font-medium"}`}
                dangerouslySetInnerHTML={{ __html: tTenant("print.body") }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {(
                [
                  ["stat1", "stat1sub"],
                  ["stat2", "stat2sub"],
                  ["stat3", "stat3sub"],
                  ["stat4", "stat4sub"],
                ] as const
              ).map(([val, sub]) => (
                <div key={val} className={`rounded p-6 border transition-transform hover:scale-105 duration-300 ${isChlorine ? "bg-white/5 border-white/10" : "bg-white/10 border-white/20 shadow-lg"}`}>
                  <p className="font-display text-2xl font-bold text-white mb-1">
                    {tTenant(`print.${val}`)}
                  </p>
                  <p className={`text-[10px] uppercase tracking-widest font-bold ${isChlorine ? "text-gray-500" : "text-white/60"}`}>{tTenant(`print.${sub}`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Card Creator promo */}
      <AiCardPromoBanner />

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 text-center">
          {(["design", "print", "ship", "pay"] as const).map((key) => (
            <div key={key} className="flex flex-col items-center gap-4">
              <span className="text-4xl grayscale hover:grayscale-0 transition-all duration-500 cursor-default">{valuePropIcon(key)}</span>
              <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs">{tTenant(`valueProps.${key}.title`)}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{tTenant(`valueProps.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function categoryEmoji(slug: string) {
  const map: Record<string, string> = {
    // Chlolo Designs
    birthday: "🎂",
    anniversary: "💑",
    "thank-you": "🙏",
    holiday: "🎄",
    sympathy: "🕊️",
    wedding: "💍",
    // Chlorine Fine Art
    "industrial-archetype": "⚙️",
    "edo-reimagined": "⛩️",
    "botanical-precision": "🌿",
    "structural-aesthetics": "🏛️",
  };
  return map[slug] ?? "🖼️";
}

function valuePropIcon(key: string) {
  const map: Record<string, string> = {
    design: "🎨",
    print: "🖨️",
    ship: "📦",
    pay: "💳",
  };
  return map[key] ?? "✨";
}
